import { useRef, useState } from 'react';
import { AGENTS, type Agent, type Chat } from '@/mocks/chat';
import AgentPicker from './AgentPicker';
import ModelSelect from './ModelSelect';
import type { LLMSettings, ProjectFile } from '@/api';
import { PROJECT_FILE_ACCEPT } from '@/api';
import { isLoggedIn } from '@/auth/token';
import HostedCreditsCTA from './HostedCreditsCTA';
import type { HostedCreditsMode } from '../hostedCredits';

type Props = {
  chat: Chat;
  settings: LLMSettings | null;
  models: string[];
  loadedModels: string[];
  savingSettings: boolean;
  settingsMsg: string | null;
  hostedCredits?: HostedCreditsMode;
  onSetAgent: (id: Agent['id']) => void;
  onSetModel: (model: string) => void;
  onSetBaseUrl: (url: string) => void;
  onSetContext: (context: string) => void;
  onSetOpenAIKey: (key: string) => void;
  onSetAnthropicKey: (key: string) => void;
  openaiKey: string;
  anthropicKey: string;
  onSaveSettings: () => void;
  onRefreshModels: () => void;
  projectId: string | null;
  projectFiles: ProjectFile[];
  projectFilesBusy?: boolean;
  projectFilesError?: string | null;
  onAddProjectFiles: (files: File[]) => void;
  onRemoveProjectFile: (id: string) => void;
  onCreateSkill: (input: { name: string; description: string; body: string }) => Promise<void>;
};

export default function AgentSettings({
  chat,
  settings,
  models,
  loadedModels,
  savingSettings,
  settingsMsg,
  hostedCredits,
  onSetAgent,
  onSetModel,
  onSetBaseUrl,
  onSetContext,
  onSetOpenAIKey,
  onSetAnthropicKey,
  openaiKey,
  anthropicKey,
  onSaveSettings,
  onRefreshModels,
  projectId,
  projectFiles,
  projectFilesBusy,
  projectFilesError,
  onAddProjectFiles,
  onRemoveProjectFile,
  onCreateSkill,
}: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const agent = AGENTS.find((a) => a.id === chat.agentId);
  const [skillName, setSkillName] = useState('');
  const [skillDesc, setSkillDesc] = useState('');
  const [skillBody, setSkillBody] = useState('');
  const [skillBusy, setSkillBusy] = useState(false);
  const [skillErr, setSkillErr] = useState<string | null>(null);

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!files.length) return;
    onAddProjectFiles(files);
  };

  const submitSkill = async () => {
    const name = skillName.trim();
    const description = skillDesc.trim();
    if (!name || !description || skillBusy) return;
    setSkillBusy(true);
    setSkillErr(null);
    try {
      await onCreateSkill({ name, description, body: skillBody.trim() });
      setSkillName('');
      setSkillDesc('');
      setSkillBody('');
    } catch (err) {
      setSkillErr(err instanceof Error ? err.message : 'could not create skill');
    } finally {
      setSkillBusy(false);
    }
  };

  return (
    <aside className="w-80 shrink-0 border-l border-border bg-bg overflow-y-auto">
      <div className="p-4 space-y-5">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-texts mb-2">Agent</div>
          <AgentPicker value={chat.agentId} onChange={onSetAgent} testId="agent-picker" />
          {agent && <p className="text-xs text-texts mt-2 leading-relaxed">{agent.description}</p>}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Model</div>
          <ModelSelect chat={chat} models={models} loadedModels={loadedModels} onSetModel={onSetModel} />
        </div>

        {chat.agentId === 'lmstudio' && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">LM Studio base URL</div>
            <input
              value={chat.baseUrl}
              onChange={(e) => onSetBaseUrl(e.target.value)}
              placeholder="http://127.0.0.1:1234/v1"
              className="w-full bg-surface/60 border border-border rounded-md px-2.5 py-2 text-sm text-textp placeholder:text-texts outline-none focus:border-accent font-mono"
            />
            <button
              onClick={onRefreshModels}
              className="mt-2 whitespace-nowrap flex items-center gap-1.5 text-xs text-texts hover:text-textp"
            >
              <i className="ri-refresh-line" /> Connect / refresh models
            </button>
            <p className="text-[11px] text-texts mt-1.5 leading-relaxed">
              OpenAI-compatible local server. Start it in LM Studio (Developer), pick a loaded model, no API key needed.
            </p>
          </div>
        )}

        {chat.agentId === 'openai' && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">OpenAI API key</div>
            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
              <input
                type="password"
                name="openai-api-key"
                autoComplete="new-password"
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
                value={openaiKey}
                onChange={(e) => onSetOpenAIKey(e.target.value)}
                placeholder={settings?.openai_key_set ? '•••• saved on account' : 'sk-...'}
                className="w-full appearance-none bg-surface/60 border border-border rounded-md px-2.5 py-2 text-sm text-textp placeholder:text-texts outline-none focus:border-accent font-mono"
              />
            </form>
          </div>
        )}

        {chat.agentId === 'anthropic' && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Anthropic API key</div>
            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
              <input
                type="password"
                name="anthropic-api-key"
                autoComplete="new-password"
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
                value={anthropicKey}
                onChange={(e) => onSetAnthropicKey(e.target.value)}
                placeholder={settings?.anthropic_key_set ? '•••• saved on account' : 'sk-ant-...'}
                className="w-full appearance-none bg-surface/60 border border-border rounded-md px-2.5 py-2 text-sm text-textp placeholder:text-texts outline-none focus:border-accent font-mono"
              />
            </form>
          </div>
        )}

        {chat.agentId === 'openrouter' && (
          <div>
            <button
              onClick={onRefreshModels}
              className="mt-2 whitespace-nowrap flex items-center gap-1.5 text-xs text-texts hover:text-textp"
            >
              <i className="ri-refresh-line" /> Refresh models
            </button>
            <p className="text-[11px] text-texts mt-1.5 leading-relaxed">
              Hosted completions spend credits. Claude and OpenAI stay on your own keys.
            </p>
            {hostedCredits && (
              <div className="mt-2">
                <HostedCreditsCTA mode={hostedCredits} />
              </div>
            )}
          </div>
        )}

        <div>
          <button
            onClick={onSaveSettings}
            disabled={savingSettings}
            className="w-full flex items-center justify-center gap-1.5 bg-surface hover:bg-surface2 border border-border text-textp text-xs font-medium px-2.5 py-1.5 rounded-md disabled:opacity-60"
          >
            {savingSettings ? <><i className="ri-loader-4-line animate-spin" /> Saving</> : <><i className="ri-save-line" /> {isLoggedIn() ? 'Save to account' : chat.agentId === 'lmstudio' ? 'Keep in this browser' : chat.agentId === 'openrouter' ? 'Sign in to use hosted models' : 'Sign in to save keys'}</>}
          </button>
          {settingsMsg && <div className="mt-1.5 text-[11px] text-texts">{settingsMsg}</div>}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Extra context</div>
          <textarea
            value={chat.context}
            onChange={(e) => onSetContext(e.target.value)}
            placeholder="Constraints, tone, or what you are trying to ship..."
            className="w-full bg-surface/60 border border-border rounded-md px-2.5 py-2 text-sm text-textp placeholder:text-texts outline-none focus:border-accent resize-y min-h-[96px]"
          />
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-texts mb-2">Files</div>
          <div className="flex gap-2 mb-2.5">
            <button
              onClick={() => fileInput.current?.click()}
              disabled={!projectId || !!projectFilesBusy}
              className="whitespace-nowrap flex items-center gap-1.5 bg-surface hover:bg-surface2 disabled:opacity-50 border border-border text-textp text-xs font-medium px-2.5 py-1.5 rounded-md"
            >
              <i className="ri-file-add-line" /> Add Files
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={PROJECT_FILE_ACCEPT}
              className="hidden"
              onChange={handleFiles}
            />
          </div>
          {!projectId ? (
            <div className="text-xs text-texts text-center py-5 border border-dashed border-border rounded-md">
              Open a project to attach files. They are included in every Chat and Run.
            </div>
          ) : projectFiles.length === 0 ? (
            <div className="text-xs text-texts text-center py-5 border border-dashed border-border rounded-md">
              Optional extra files. The canvas and catalog are already in context.
            </div>
          ) : (
            <ul className="space-y-1">
              {projectFiles.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center gap-2 bg-surface/60 border border-border rounded-md px-2.5 py-1.5 group"
                >
                  <i className="ri-file-text-line text-texts text-sm shrink-0" />
                  <span className="text-xs text-textp truncate flex-1 font-mono">{f.name}</span>
                  <button
                    onClick={() => onRemoveProjectFile(f.id)}
                    className="opacity-0 group-hover:opacity-100 text-texts hover:text-error shrink-0"
                    title="Remove"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {projectFilesError && <div className="mt-1.5 text-[11px] text-error">{projectFilesError}</div>}
        </div>

        <div className="pt-2 border-t border-border">
          <div className="text-[11px] uppercase tracking-wider text-texts mb-2">Create a skill</div>
          <input
            value={skillName}
            onChange={(e) => setSkillName(e.target.value)}
            placeholder="Name"
            className="w-full bg-surface/60 border border-border rounded-md px-2.5 py-1.5 text-sm text-textp placeholder:text-texts outline-none focus:border-accent mb-1.5"
          />
          <input
            value={skillDesc}
            onChange={(e) => setSkillDesc(e.target.value)}
            placeholder="One-line description"
            className="w-full bg-surface/60 border border-border rounded-md px-2.5 py-1.5 text-sm text-textp placeholder:text-texts outline-none focus:border-accent mb-1.5"
          />
          <textarea
            value={skillBody}
            onChange={(e) => setSkillBody(e.target.value)}
            placeholder="Instructions the agent should follow (optional)"
            rows={3}
            className="w-full bg-surface/60 border border-border rounded-md px-2.5 py-1.5 text-sm text-textp placeholder:text-texts outline-none focus:border-accent resize-y mb-1.5"
          />
          <button
            onClick={submitSkill}
            disabled={skillBusy || !skillName.trim() || !skillDesc.trim()}
            className="w-full flex items-center justify-center gap-1.5 bg-accent hover:bg-accent-hover disabled:opacity-60 text-accent-text text-xs font-medium px-2.5 py-1.5 rounded-md"
          >
            {skillBusy ? <><i className="ri-loader-4-line animate-spin" /> Creating</> : <><i className="ri-add-line" /> Add to catalog</>}
          </button>
          {skillErr && <div className="mt-1 text-[11px] text-error">{skillErr}</div>}
        </div>
      </div>
    </aside>
  );
}

